import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IonList, IonItem, IonLabel, IonIcon } from '@ionic/react';
import {
  checkmarkCircle,
  checkmarkCircleOutline,
  closeCircle,
  closeCircleOutline,
  createOutline,
  eyeOutline,
  removeCircleOutline,
  timeOutline,
  trashOutline,
} from 'ionicons/icons';
import { BILL_POSITIONS, type BillPosition } from '../../../types/bill';
import ProPageModal from './ProPageModal';

type IonColor = 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger' | 'medium';

interface PositionOption {
  value: string;
  label: string;
  color: IonColor;
  icon: string;
  description?: string;
}

/** Visual metadata for canonical bill stored values — labels come from i18n. */
const BILL_OPTION_META: Record<
  BillPosition,
  { color: IonColor; icon: string; labelKey: string }
> = {
  Monitor: { color: 'medium', icon: eyeOutline, labelKey: 'positions.monitor' },
  Support: { color: 'success', icon: checkmarkCircleOutline, labelKey: 'positions.support' },
  StrSupport: { color: 'success', icon: checkmarkCircle, labelKey: 'positions.strSupport' },
  Neutral: { color: 'medium', icon: removeCircleOutline, labelKey: 'positions.neutral' },
  Oppose: { color: 'danger', icon: closeCircleOutline, labelKey: 'positions.oppose' },
  StrOppose: { color: 'danger', icon: closeCircle, labelKey: 'positions.strOppose' },
  Amend: { color: 'warning', icon: createOutline, labelKey: 'positions.amend' },
  Pending: { color: 'warning', icon: timeOutline, labelKey: 'positions.pending' },
};

export interface ProPositionSheetProps {
  isOpen: boolean;
  currentPosition: string | null;
  onSelect: (position: string | null) => void;
  onDismiss: () => void;
  type: 'bill' | 'legislator';
}

/**
 * Pro mobile position picker presented as a full-page modal.
 *
 * Drop-in replacement for `ProPositionSelector` (IonActionSheet-based). This
 * version uses the reusable `ProPageModal` primitive so the position
 * options can be rendered as full `IonItem` rows with icons, descriptions,
 * and the current selection visually highlighted.
 *
 * Bill options are driven by `BILL_POSITIONS` so native saves the
 * same canonical stored values web already uses. Legislator options keep
 * their separate vocabulary (Support / Oppose / Watch / Neutral).
 *
 * Behavior differences vs. ProPositionSelector:
 * - Backdrop tap or close button calls `onDismiss` (does NOT call `onSelect`).
 *   This fixes the old "Cancel routes through onSelect(currentPosition)"
 *   quirk that triggered a spurious position mutation on every dismiss.
 * - Clear Position is a separate destructive button at the bottom.
 */
const ProPositionSheet: React.FC<ProPositionSheetProps> = ({
  isOpen,
  currentPosition,
  onSelect,
  onDismiss,
  type,
}) => {
  const { t } = useTranslation('pro');

  const billOptions: PositionOption[] = useMemo(
    () =>
      BILL_POSITIONS.map(({ value }) => {
        const meta = BILL_OPTION_META[value];
        return {
          value,
          label: t(meta.labelKey),
          color: meta.color,
          icon: meta.icon,
        };
      }),
    [t],
  );

  const legislatorOptions: PositionOption[] = [
    {
      value: 'Support',
      label: t('positions.support'),
      color: 'success',
      icon: checkmarkCircleOutline,
    },
    {
      value: 'Oppose',
      label: t('positions.oppose'),
      color: 'danger',
      icon: closeCircleOutline,
    },
    {
      value: 'Watch',
      label: t('positions.watch'),
      color: 'primary',
      icon: eyeOutline,
    },
    {
      value: 'Neutral',
      label: t('positions.neutral'),
      color: 'medium',
      icon: removeCircleOutline,
    },
  ];

  const options = type === 'bill' ? billOptions : legislatorOptions;

  const handlePick = (value: string) => {
    onSelect(value);
  };

  const handleClear = () => {
    onSelect(null);
  };

  return (
    <ProPageModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={t('bottomSheet.setPositionTitle')}
    >
      <IonList lines="inset">
        {options.map((opt) => {
          const selected = currentPosition === opt.value;
          return (
            <IonItem
              key={opt.value}
              button
              onClick={() => handlePick(opt.value)}
              detail={false}
            >
              <IonIcon
                icon={opt.icon}
                slot="start"
                color={opt.color}
                className="text-xl"
              />
              <IonLabel>
                <h3 className="text-sm font-semibold">{opt.label}</h3>
                {opt.description && (
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {opt.description}
                  </p>
                )}
              </IonLabel>
              {selected && (
                <IonIcon slot="end" icon={checkmarkCircle} color="primary" />
              )}
            </IonItem>
          );
        })}
      </IonList>

      {currentPosition && (
        <div className="px-4 py-4">
          <button
            type="button"
            onClick={handleClear}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-red-300 dark:border-red-400/40 bg-transparent text-red-600 dark:text-red-400 font-semibold text-sm transition-colors active:bg-red-50 dark:active:bg-red-900/20"
          >
            <IonIcon icon={trashOutline} className="text-lg" />
            {t('positions.clearPosition')}
          </button>
        </div>
      )}
    </ProPageModal>
  );
};

export default ProPositionSheet;
